<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: null,
                    },
                ]"
            />
        </template>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $tc('messages.levels', 2) }}
            </template>
            <details class="mt-3 sm:col-span-12">
                <summary>
                    <h2 class="inline-block text-lg leading-6 font-medium text-gray-900 mb-3">
                        {{ $tc('messages.levels', 2) }}
                    </h2>
                </summary>

                <div class="flex flex-col mt-5">
                    <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                        <div
                            class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                        >
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $tc('messages.numbers', 1) }}
                                        </th>
                                        <th
                                            class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $tc('messages.experience_points', 2) }}
                                        </th>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $tc('messages.ranks', 1) }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                        v-for="(level, index) in gamificationSet.levels"
                                        :key="`levels_${index}`"
                                    >
                                        <td
                                            class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            {{ index }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                            {{ level.width }} [{{ level.min }} - {{ level.max }})
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                            {{ level.rank }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </details>
        </TitleFieldsFormSection>

        <div v-if="gamificationSet.rewards.length > 0">
            <div class="text-xl flex gap-x-2">
                <span>{{ $tc('messages.gamification_rewards', 2) }}</span>
                <span class="ml-2">({{ form.reward_ids.length }} / {{ gamificationSet.rewards.length }})</span>

                <SelectAllNoneButtons
                    :max-count="gamificationSet.rewards.length"
                    :selected-count="form.reward_ids.length"
                    @select-all="selectAll('reward_ids')"
                    @select-none="form.reward_ids = []"
                    class="flex"
                />
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-6 mt-5">
                <div v-for="reward in gamificationSet.rewards" class="flex flex-col mt-5 sm:mt-0" :key="reward.id">
                    <div class="text-sm">
                        <div class="flex space-x-2">
                            <input
                                type="checkbox"
                                name="selected_rewards"
                                v-model="form.reward_ids"
                                :value="reward.id"
                                :id="`reward_${reward.id}`"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />

                            <label :for="`reward_${reward.id}`">
                                <span v-if="reward.creates_foul">
                                    <svg
                                        class="inline-block h-4 w-4 text-gray-600 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                        stroke="none"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M18.615 3.13745H5.38496C4.11496 3.13745 3.08496 4.16745 3.08496 5.43745V18.6675C3.08496 19.9375 4.11496 20.9675 5.38496 20.9675H18.615C19.885 20.9675 20.915 19.9375 20.915 18.6675V5.43745C20.915 4.16745 19.885 3.13745 18.615 3.13745ZM19.715 18.6575C19.715 19.2675 19.225 19.7575 18.615 19.7575H5.38496C4.77496 19.7575 4.28496 19.2675 4.28496 18.6575V5.43745C4.28496 4.82745 4.77496 4.33745 5.38496 4.33745H18.615C19.225 4.33745 19.715 4.82745 19.715 5.43745V18.6575ZM12.5149 5.26758H11.4849C10.9749 5.26758 10.4749 5.48758 10.1249 5.87758C9.75494 6.28758 9.56494 6.85758 9.62494 7.41758L10.1349 12.4076C10.2349 13.3976 11.0349 14.1476 11.9949 14.1476C12.9549 14.1476 13.7449 13.3976 13.8549 12.4076L14.3649 7.41758C14.4249 6.84758 14.2449 6.28758 13.8649 5.87758C13.5249 5.49758 13.0249 5.26758 12.5149 5.26758ZM12.6649 12.2876C12.6249 12.6676 12.3449 12.9476 12.0049 12.9476C11.6649 12.9476 11.3849 12.6676 11.3449 12.2876L10.8349 7.29758C10.8149 7.06758 10.8849 6.84758 11.0249 6.67758C11.1549 6.53758 11.3149 6.45758 11.4949 6.45758H12.5249C12.7049 6.45758 12.8649 6.53758 12.9949 6.67758C13.1449 6.83758 13.2149 7.06758 13.1849 7.29758L12.6649 12.2876ZM10.205 16.9775C10.205 15.9875 11.015 15.1875 12.005 15.1875C12.995 15.1875 13.805 15.9875 13.805 16.9775C13.805 17.9675 12.995 18.7675 12.005 18.7675C11.015 18.7675 10.205 17.9675 10.205 16.9775ZM11.205 16.9775C11.205 17.4075 11.565 17.7675 12.005 17.7675C12.445 17.7675 12.805 17.4175 12.805 16.9775C12.805 16.5475 12.445 16.1875 12.005 16.1875C11.565 16.1875 11.205 16.5375 11.205 16.9775Z"
                                        />
                                    </svg>
                                </span>

                                <span class="text-sm">{{ reward.title }}</span>
                            </label>
                        </div>
                    </div>
                    <div class="mt-2">
                        <div
                            :class="[reward.is_negative ? 'bg-red-500' : 'bg-indigo-500']"
                            class="shrink-0 flex items-center justify-center h-16 w-full rounded-lg aspect-w-1 aspect-h-1"
                        >
                            <ZoomableImage :src="reward.image_path">
                                <img loading="lazy" :src="reward.image_path" alt="" />
                            </ZoomableImage>
                        </div>
                    </div>
                    <div class="flex justify-between mt-2">
                        <div>
                            <BadgePill color="green" class="text-lg" v-if="reward.experience_amount !== null">
                                {{ reward.experience_amount }}
                            </BadgePill>
                        </div>
                        <div>
                            <BadgePill color="red" class="text-lg" v-if="reward.health_amount !== null">
                                {{ reward.health_amount }}
                            </BadgePill>
                        </div>
                        <div>
                            <BadgePill color="yellow" class="text-lg" v-if="reward.coins_amount !== null">
                                {{ reward.coins_amount }}
                            </BadgePill>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="gamificationSet.items.length > 0" class="mt-5">
            <div class="text-xl flex gap-x-2">
                {{ $tc('messages.gamification_items', 2) }}
                <span class="ml-2">({{ form.item_ids.length }} / {{ gamificationSet.items.length }})</span>

                <SelectAllNoneButtons
                    :max-count="gamificationSet.items.length"
                    :selected-count="form.item_ids.length"
                    @select-all="selectAll('item_ids')"
                    @select-none="form.item_ids = []"
                    class="flex"
                />
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-6 mt-5">
                <AppCard v-for="item in gamificationSet.items" :key="`${item.id}_${item.count}`">
                    <div class="p-1">
                        <div class="flex gap-x-1">
                            <input
                                type="checkbox"
                                name="selected_rewards"
                                v-model="form.item_ids"
                                :value="item.id"
                                :id="`item_${item.id}`"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            {{ item.name }}
                        </div>
                        <ZoomableImage :src="item.image_path">
                            <img loading="lazy" :src="item.image_path" alt="" />
                        </ZoomableImage>
                        <div class="text-gray-600 text-sm">
                            {{ item.options_description }}
                        </div>
                        <div class="flex gap-x-2">
                            <div v-if="item.description">
                                <Tippy>
                                    <button>
                                        <IconInfoCircle class="w-6 h-6 text-gray-400" />
                                    </button>
                                    <template #content>
                                        {{ item.description }}
                                    </template>
                                </Tippy>
                            </div>
                        </div>
                        <div class="flex justify-between items-center mt-1">
                            <div>
                                <BadgePill color="yellow">
                                    {{ item.price }}
                                </BadgePill>
                            </div>
                        </div>
                        <div class="flex justify-between items-center mt-1" v-if="item.required_level_description">
                            {{ $t('messages.level_required') }}:
                            {{ item.required_level_description }}
                        </div>
                    </div>
                </AppCard>
            </div>
        </div>

        <div v-if="gamificationSet.card_collections.length > 0" class="mt-5">
            <div class="text-xl flex gap-x-2">
                {{ $tc('messages.card_collections', 2) }}
                <span class="ml-2"
                    >({{ form.card_collection_ids.length }} / {{ gamificationSet.card_collections.length }})</span
                >

                <SelectAllNoneButtons
                    :max-count="gamificationSet.card_collections.length"
                    :selected-count="form.card_collection_ids.length"
                    @select-all="selectAll('card_collection_ids')"
                    @select-none="form.card_collection_ids = []"
                    class="flex"
                />
            </div>
            <div>
                <div class="text-gray-600 flex gap-x-2">
                    <div>
                        <CardRarityIndicator rarity="common" class="inline-block" />
                        {{ $t('messages.common') }}
                    </div>
                    <div>
                        <CardRarityIndicator rarity="uncommon" class="inline-block" />
                        {{ $t('messages.uncommon') }}
                    </div>
                    <div>
                        <CardRarityIndicator rarity="rare" class="inline-block" />
                        {{ $t('messages.rare') }}
                    </div>
                </div>
            </div>
            <div class="lg:grid lg:grid-cols-2 md:gap-6 bg-gray-50 p-2 mt-3">
                <AppCard
                    class="col-span-1 bg-white rounded-lg shadow divide-y divide-gray-200"
                    v-for="card_collection in gamificationSet.card_collections"
                    :key="card_collection.id"
                >
                    <div class="flex space-x-2">
                        <div
                            class="w-10 h-10 bg-gray-300 rounded-full shrink-0 relative bg-no-repeat bg-center bg-cover"
                            :style="`background-image: url(${card_collection.image_path});`"
                        ></div>
                        <div class="flex items-center space-x-3 justify-between">
                            <div class="text-gray-900 text-sm font-medium truncate">
                                <input
                                    type="checkbox"
                                    name="selected_rewards"
                                    v-model="form.card_collection_ids"
                                    :value="card_collection.id"
                                    :id="`reward_${card_collection.id}`"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                {{ card_collection.name }}
                            </div>
                        </div>
                    </div>
                    <div class="w-full flex items-center justify-between p-6 space-x-6">
                        <div class="flex-1 truncate">
                            <div class="grid grid-cols-4 md:grid-cols-6 gap-2 md:gap-6 mt-1">
                                <div v-for="card in card_collection.cards" class="" :title="card.name" :key="card.id">
                                    <div class="w-full relative aspect-w-1 aspect-h-1">
                                        <ZoomableImage :src="card.image_path">
                                            <img class="w-full h-full object-contain" :src="card.image_path" alt="" />
                                        </ZoomableImage>
                                    </div>
                                    <div class="flex items-center justify-between mt-1">
                                        <CardRarityIndicator :rarity="card.rarity" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </AppCard>
            </div>
        </div>

        <div v-if="gamificationSet.badges.length > 0">
            <div class="text-xl flex gap-x-2">
                {{ $tc('messages.badges', 2) }}
                <span class="ml-2">({{ form.badge_ids.length }} / {{ gamificationSet.badges.length }})</span>

                <SelectAllNoneButtons
                    :max-count="gamificationSet.badges.length"
                    :selected-count="form.badge_ids.length"
                    @select-all="selectAll('badge_ids')"
                    @select-none="form.badge_ids = []"
                    class="flex"
                />
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-6 mt-5">
                <div v-for="badge in gamificationSet.badges" class="flex flex-col mt-5 sm:mt-0" :key="badge.id">
                    <div class="text-sm">
                        <div class="flex space-x-2">
                            <input
                                type="checkbox"
                                name="selected_badges"
                                v-model="form.badge_ids"
                                :value="badge.id"
                                :id="`badge_${badge.id}`"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />

                            <div class="text-sm">{{ badge.name }}</div>
                        </div>
                    </div>
                    <div class="mt-2">
                        <div class="rounded-full p-2 bg-indigo-500 flex justify-center items-center">
                            <ZoomableImage :src="badge.image_path">
                                <img class="rounded-full" loading="lazy" :src="badge.image_path" alt="" />
                            </ZoomableImage>
                        </div>
                    </div>
                    <div class="text-xs">{{ badge.description }}</div>
                </div>
            </div>
        </div>

        <div class="mt-5 flex justify-end">
            <AppWell>
                <AppCard>
                    <div class="p-6">
                        <ul class="text-sm">
                            <li class="flex justify-between odd:bg-white even:bg-gray-50 py-1 px-2">
                                <div class="flex gap-x-1">
                                    <div>
                                        <GamificationSectionIcon class="mr-1 text-gray-400 h-4 w-4" section="rewards" />
                                    </div>
                                    <div>{{ $tc('messages.gamification_rewards', 2) }}</div>
                                </div>
                                <div class="ml-2">
                                    ({{ form.reward_ids.length }} / {{ gamificationSet.rewards.length }})
                                </div>
                            </li>
                            <li class="flex justify-between odd:bg-white even:bg-gray-50 py-1 px-2">
                                <div class="flex gap-x-1">
                                    <div>
                                        <GamificationSectionIcon class="mr-1 text-gray-400 h-4 w-4" section="items" />
                                    </div>
                                    <div>{{ $tc('messages.gamification_items', 2) }}</div>
                                </div>
                                <div class="ml-2">
                                    ({{ form.item_ids.length }} / {{ gamificationSet.items.length }})
                                </div>
                            </li>
                            <li class="flex justify-between odd:bg-white even:bg-gray-50 py-1 px-2">
                                <div class="flex gap-x-1">
                                    <div>
                                        <GamificationSectionIcon
                                            class="mr-1 text-gray-400 h-4 w-4"
                                            section="card_collections"
                                        />
                                    </div>
                                    <div>{{ $tc('messages.card_collections', 2) }}</div>
                                </div>
                                <div class="ml-2">
                                    ({{ form.card_collection_ids.length }} /
                                    {{ gamificationSet.card_collections.length }})
                                </div>
                            </li>
                            <li class="flex justify-between odd:bg-white even:bg-gray-50 py-1 px-2">
                                <div class="flex gap-x-1">
                                    <div>
                                        <GamificationSectionIcon class="mr-1 text-gray-400 h-4 w-4" section="badges" />
                                    </div>
                                    <div>{{ $tc('messages.badges', 2) }}</div>
                                </div>
                                <div class="ml-2">
                                    ({{ form.badge_ids.length }} / {{ gamificationSet.badges.length }})
                                </div>
                            </li>
                        </ul>

                        <div class="mt-3">
                            <FormLabel :value="$t('messages.grade_level')" for="grade_level_ids" />
                            <div class="mt-1">
                                <label
                                    v-for="grade_level in options.grade_levels"
                                    class="mr-1"
                                    :key="grade_level.value"
                                >
                                    <input
                                        v-model="form.grade_level_ids"
                                        name="grade_levels"
                                        :value="grade_level.value"
                                        type="checkbox"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    />
                                    {{ grade_level.text }}
                                </label>
                            </div>
                            <FormError :value="form.errors.grade_level_ids" class="mt-2" />
                        </div>

                        <div class="flex mt-3">
                            <form action="" @submit.prevent="submit">
                                <SubmitFormButton :button-text="$t('messages.import')" :processing="form.processing" />
                            </form>
                        </div>

                        <!--            <AppButton element="InertiaLink" href="http://attendance.test/_/sets/l9avmeG1"> reload </AppButton>-->
                    </div>
                </AppCard>
            </AppWell>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import BadgePill from '@/components/BadgePill.vue';
import ZoomableImage from '@/components/ZoomableImage.vue';
import { IconInfoCircle } from '@tabler/icons-vue';
import AppCard from '@/Shared/AppCard.vue';
import { Tippy } from 'vue-tippy';
import CardRarityIndicator from '@/components/CardRarityIndicator.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import AppWell from '@/Shared/AppWell.vue';
import GamificationSectionIcon from '@/components/Gamification/GamificationSectionIcon.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';

export default {
    components: {
        FormLabel,
        FormError,
        SelectAllNoneButtons,
        GamificationSectionIcon,
        AppWell,
        SubmitFormButton,
        CardRarityIndicator,
        IconInfoCircle,
        Tippy,
        AppCard,
        ZoomableImage,
        BadgePill,
        PanelPage,
        TitleFieldsFormSection,
    },
    props: ['meta', 'gamificationSet', 'options'],
    layout: Layout,
    data() {
        return {
            form: this.$inertia.form({
                reward_ids: this.gamificationSet.reward_ids,
                item_ids: this.gamificationSet.item_ids,
                card_collection_ids: this.gamificationSet.card_collection_ids,
                badge_ids: this.gamificationSet.badge_ids,
                grade_level_ids: [],
            }),
        };
    },
    name: 'Show',
    computed: {},
    methods: {
        submit() {
            this.form.post(this.meta.links.store);
        },
        selectAll(section) {
            this.form[section] = this.gamificationSet[section];
        },
    },
};
</script>
